import type { SdkWidgetSettings } from '../types';

/**
 * Generates the full CSS block for the Veridion consent widget.
 * Injects CSS custom properties based on the tenant's widget settings
 * so colours / positioning can be configured from the admin panel.
 */
export function buildStyles(settings: SdkWidgetSettings): string {
  const accent = settings.accentColor || '#4f46e5';
  const position = (settings.position || 'bottom').toLowerCase();
  const isLight = (settings.theme || 'light').toLowerCase() === 'light';
  const overlayStyle = (settings.overlayStyle || 'blur').toLowerCase();
  const useOverlayBlur = overlayStyle === 'blur';
  const cornerRadius = Math.min(24, Math.max(0, settings.buttonCornerRadius ?? 6));
  const buttonOutline = settings.buttonOutline ?? false;

  const bg = isLight ? '#ffffff' : '#1e1e2e';
  const text = isLight ? '#1f2937' : '#e5e7eb';
  const heading = isLight ? '#111827' : '#f3f4f6';
  const link = isLight ? accent : '#f69420';
  const muted = isLight ? '#6b7280' : '#9ca3af';
  const border = isLight ? '#e5e7eb' : '#374151';
  const overlayBg = 'rgba(0,0,0,0.45)';
  const surfaceHover = isLight ? '#f3f4f6' : '#2a2a3d';

  const bannerPlacement =
    position === 'top'
      ? 'top: 0; bottom: auto;'
      : 'bottom: 0; top: auto;';

  const overlayBlurCss = useOverlayBlur
    ? 'backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);'
    : '';

  return /* css */ `
    :host {
      --vrd-accent: ${accent};
      --vrd-bg: ${bg};
      --vrd-text: ${text};
      --vrd-heading: ${heading};
      --vrd-link: ${link};
      --vrd-muted: ${muted};
      --vrd-border: ${border};
      --vrd-overlay: ${overlayBg};
      --vrd-surface-hover: ${surfaceHover};
      --vrd-btn-radius: ${cornerRadius}px;
      --vrd-overlay-blur: ${useOverlayBlur ? 'blur(6px)' : 'none'};
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
        Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
      font-size: 14px;
      line-height: 1.5;
      color: var(--vrd-text);
      box-sizing: border-box;
    }

    *, *::before, *::after {
      box-sizing: border-box;
    }

    /* ── Banner ── */
    .vrd-banner {
      position: fixed;
      ${bannerPlacement}
      left: 0;
      right: 0;
      z-index: 2147483640;
      background: var(--vrd-bg);
      border-top: 1px solid var(--vrd-border);
      padding: 16px 24px;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 16px;
      box-shadow: 0 -2px 12px rgba(0,0,0,0.08);
      animation: vrd-slide-in 0.3s ease-out;
    }

    .vrd-banner--compact {
      padding: 10px 20px;
      gap: 12px;
    }
    .vrd-banner--compact .vrd-banner__title { font-size: 14px; }
    .vrd-banner--compact .vrd-banner__desc { font-size: 13px; }
    .vrd-banner--compact .vrd-btn { padding: 6px 14px; font-size: 13px; }

    .vrd-banner--minimal {
      left: auto;
      right: 16px;
      bottom: 16px;
      top: auto;
      width: auto;
      max-width: 380px;
      border-radius: 12px;
      border: 1px solid var(--vrd-border);
      padding: 12px 16px;
      gap: 10px;
      box-shadow: 0 4px 20px rgba(0,0,0,0.15);
      animation: vrd-minimal-in 0.25s ease-out;
    }
    .vrd-banner--minimal .vrd-banner__title { font-size: 14px; margin: 0; }
    .vrd-banner--minimal .vrd-banner__body { flex: 1 1 auto; min-width: 0; }
    .vrd-banner--minimal .vrd-banner__actions { flex-wrap: wrap; }
    .vrd-banner--minimal .vrd-btn { padding: 6px 12px; font-size: 12px; }

    .vrd-banner-overlay {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .vrd-banner-dialog-box {
      max-width: 560px;
      width: 90vw;
    }
    .vrd-banner.vrd-banner--dialog {
      position: relative;
      left: auto;
      right: auto;
      bottom: auto;
      top: auto;
      border-radius: 12px;
      border: 1px solid var(--vrd-border);
      box-shadow: 0 8px 30px rgba(0,0,0,0.18);
    }
    @keyframes vrd-minimal-in {
      from { transform: translateY(10px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }

    @keyframes vrd-slide-in {
      from { transform: translateY(${position === 'top' ? '-100%' : '100%'}); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }

    .vrd-banner__header {
      width: 100%;
      flex: 0 0 auto;
    }
    .vrd-banner__main {
      flex: 1 1 100%;
      min-width: 0;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 16px;
    }
    .vrd-banner__body {
      flex: 1 1 400px;
      min-width: 0;
    }

    .vrd-banner__title {
      font-size: 16px;
      font-weight: 600;
      margin: 0 0 4px;
      color: var(--vrd-heading);
    }

    .vrd-banner__desc {
      font-size: 14px;
      color: var(--vrd-muted);
      margin: 0;
    }

    .vrd-banner__actions {
      display: flex;
      gap: 8px;
      flex-shrink: 0;
      flex-wrap: wrap;
    }

    /* ── Buttons ── */
    .vrd-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 8px 20px;
      border-radius: var(--vrd-btn-radius);
      border: 1px solid transparent;
      font-size: 14px;
      font-weight: 500;
      cursor: pointer;
      white-space: nowrap;
      transition: background 0.15s, border-color 0.15s, color 0.15s, opacity 0.15s;
    }

    .vrd-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .vrd-btn--loading .vrd-btn__spinner {
      display: inline-block;
    }

    .vrd-btn__spinner {
      display: none;
      width: 13px;
      height: 13px;
      border: 2px solid currentColor;
      border-top-color: transparent;
      border-radius: 50%;
      flex-shrink: 0;
      animation: vrd-spin 0.65s linear infinite;
    }

    .vrd-btn--primary {
      background: var(--vrd-accent);
      color: #fff;
      border-color: var(--vrd-accent);
    }
    .vrd-btn--primary:hover {
      filter: brightness(1.1);
    }

    .vrd-btn--secondary {
      background: ${buttonOutline ? 'transparent' : 'transparent'};
      color: var(--vrd-text);
      border: ${buttonOutline ? '1px solid var(--vrd-border)' : '1px solid var(--vrd-border)'};
    }
    .vrd-btn--secondary:hover {
      background: var(--vrd-surface-hover);
    }

    .vrd-btn--link {
      background: none;
      border: none;
      color: var(--vrd-link);
      padding: 8px 12px;
      text-decoration: underline;
    }

    /* ── Modal overlay (blur or none) ── */
    .vrd-overlay {
      position: fixed;
      inset: 0;
      z-index: 2147483641;
      background: var(--vrd-overlay);
      ${overlayBlurCss}
      display: flex;
      align-items: center;
      justify-content: center;
      animation: vrd-fade-in 0.2s ease-out;
    }
    @keyframes vrd-fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    @keyframes vrd-spin {
      to { transform: rotate(360deg); }
    }

    .vrd-modal {
      display: flex;
      flex-direction: column;
      background: var(--vrd-bg);
      border-radius: 12px;
      max-width: 560px;
      width: 90vw;
      max-height: 80vh;
      overflow: hidden;
      box-shadow: 0 8px 30px rgba(0,0,0,0.18);
      animation: vrd-scale-in 0.25s ease-out;
    }
    @keyframes vrd-scale-in {
      from { transform: scale(0.95); opacity: 0; }
      to { transform: scale(1); opacity: 1; }
    }

    .vrd-modal__header {
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      align-items: stretch;
      padding: 20px 24px 12px;
      border-bottom: 1px solid var(--vrd-border);
      background: var(--vrd-bg);
    }
    .vrd-modal__header-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .vrd-modal__header-right {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .vrd-modal__title {
      font-size: 18px;
      font-weight: 600;
      margin: 0;
      color: var(--vrd-heading);
    }

    .vrd-modal__close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      cursor: pointer;
      color: var(--vrd-muted);
      padding: 4px;
      line-height: 1;
    }
    .vrd-modal__close:hover {
      color: var(--vrd-text);
    }
    .vrd-modal__close svg {
      display: block;
    }
    .vrd-modal__close.vrd-modal__close--hidden {
      display: none;
    }

    .vrd-modal__body {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      position: relative;
      padding: 16px 24px;
    }

    .vrd-modal__footer {
      flex-shrink: 0;
      display: flex;
      gap: 8px;
      justify-content: flex-end;
      padding: 12px 24px 20px;
      border-top: 1px solid var(--vrd-border);
      background: var(--vrd-bg);
    }

    /* ── Purpose toggle rows ── */
    .vrd-purpose {
      padding: 12px 0;
      border-bottom: 1px solid var(--vrd-border);
    }
    .vrd-purpose:last-child {
      border-bottom: none;
    }

    .vrd-purpose__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }

    .vrd-purpose__name {
      font-weight: 500;
      font-size: 14px;
      color: var(--vrd-heading);
    }

    .vrd-purpose__desc {
      font-size: 13px;
      color: var(--vrd-muted);
      margin: 4px 0 0;
    }

    /* ── Collapsible purpose group (parent + children) ── */
    .vrd-purpose-group {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid var(--vrd-border);
    }
    .vrd-purpose-group__header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 0;
      cursor: pointer;
      user-select: none;
      font-size: 14px;
      font-weight: 600;
      color: var(--vrd-text);
      border-radius: 6px;
      transition: background 0.15s;
    }
    .vrd-purpose-group__header:hover {
      background: var(--vrd-surface-hover);
    }
    .vrd-purpose-group__chevron {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      flex-shrink: 0;
      font-size: 12px;
      color: var(--vrd-muted);
      transition: transform 0.2s ease;
    }
    .vrd-purpose-group--collapsed .vrd-purpose-group__chevron {
      transform: rotate(-90deg);
    }
    .vrd-purpose-group__title {
      flex: 1;
      font-size: 14px;
      font-weight: 600;
      margin: 0;
      padding-left: 0;
    }
    .vrd-purpose-group__content {
      overflow: hidden;
      max-height: 1200px;
      transition: max-height 0.25s ease-out;
    }
    .vrd-purpose-group--collapsed .vrd-purpose-group__content {
      max-height: 0;
    }
    .vrd-purpose-group__content .vrd-purpose {
      margin-bottom: 0;
    }
    .vrd-purpose--child {
      padding-left: 20px;
      border-left: 2px solid var(--vrd-border);
      margin-left: 10px;
    }

    /* ── Toggle switch ── */
    .vrd-toggle {
      position: relative;
      width: 44px;
      height: 24px;
      flex-shrink: 0;
    }
    .vrd-toggle input {
      opacity: 0;
      width: 0;
      height: 0;
      position: absolute;
    }
    .vrd-toggle__track {
      position: absolute;
      inset: 0;
      background: var(--vrd-border);
      border-radius: 12px;
      cursor: pointer;
      transition: background 0.2s;
    }
    .vrd-toggle__track::after {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      width: 20px;
      height: 20px;
      background: #fff;
      border-radius: 50%;
      transition: transform 0.2s;
    }
    .vrd-toggle input:checked + .vrd-toggle__track {
      background: var(--vrd-accent);
    }
    .vrd-toggle input:checked + .vrd-toggle__track::after {
      transform: translateX(20px);
    }

    /* ── Logo ── */
    .vrd-logo {
      height: 28px;
      width: auto;
      margin-right: 8px;
      vertical-align: middle;
    }

    /* ── Policy section ── */
    .vrd-policy-summary {
      font-size: 13px;
      color: var(--vrd-muted);
      margin-top: 8px;
    }

    .vrd-policy-link {
      color: var(--vrd-link);
      text-decoration: underline;
      cursor: pointer;
      font-size: 13px;
    }
    .vrd-policy-link:hover {
      text-decoration-thickness: 2px;
    }

    .vrd-policy-view-btn {
      padding-left: 0;
      font-size: 13px;
    }

    /* ── Policy viewer (browse versions inside modal) ── */
    .vrd-policy-viewer {
      position: absolute;
      inset: 0;
      background: var(--vrd-bg);
      display: flex;
      flex-direction: column;
      padding: 0 24px 16px;
      opacity: 0;
      visibility: hidden;
      pointer-events: none;
      transition: opacity 0.2s, visibility 0.2s;
      z-index: 1;
    }
    .vrd-policy-viewer--visible {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
    }
    .vrd-policy-viewer__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 12px 0;
      border-bottom: 1px solid var(--vrd-border);
      flex-shrink: 0;
    }
    .vrd-policy-viewer__title {
      font-size: 16px;
      font-weight: 600;
      margin: 0;
      color: var(--vrd-heading);
    }
    .vrd-policy-viewer__back {
      background: none;
      border: none;
      color: var(--vrd-link);
      cursor: pointer;
      font-size: 14px;
      padding: 4px 8px;
    }
    .vrd-policy-viewer__back:hover {
      text-decoration: underline;
    }
    .vrd-policy-viewer__version-row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 0;
      flex-shrink: 0;
    }
    .vrd-policy-viewer__version-label {
      font-size: 13px;
      color: var(--vrd-muted);
    }
    .vrd-policy-viewer__select {
      padding: 6px 10px;
      border: 1px solid var(--vrd-border);
      border-radius: 6px;
      background: var(--vrd-bg);
      color: var(--vrd-text);
      font-size: 13px;
      cursor: pointer;
    }
    .vrd-policy-viewer__content {
      flex: 1;
      overflow-y: auto;
      min-height: 0;
      font-size: 13px;
      line-height: 1.6;
      color: var(--vrd-text);
    }
    .vrd-policy-viewer__content p {
      margin: 0 0 0.75em;
    }
    .vrd-policy-viewer__content h1, .vrd-policy-viewer__content h2, .vrd-policy-viewer__content h3 {
      margin: 1em 0 0.5em;
    }
    .vrd-policy-viewer__content ul, .vrd-policy-viewer__content ol {
      margin: 0.5em 0;
      padding-left: 1.5em;
    }
    .vrd-policy-viewer__content a {
      color: var(--vrd-link);
      text-decoration: underline;
    }
    .vrd-policy-viewer__content a:hover {
      text-decoration-thickness: 2px;
    }

    /* ── Branding (Veridion powered-by image) ── */
    .vrd-branding {
      display: flex;
      align-items: center;
      margin-top: 12px;
      padding-top: 8px;
      border-top: 1px solid var(--vrd-border);
    }
    .vrd-branding__img {
      display: block;
      height: 20px;
      width: auto;
      max-width: 100%;
      object-fit: contain;
    }
    /* Theme auto: show light or dark image based on prefers-color-scheme */
    .vrd-branding--auto .vrd-branding__img--light {
      display: block;
    }
    .vrd-branding--auto .vrd-branding__img--dark {
      display: none;
    }
    @media (prefers-color-scheme: dark) {
      .vrd-branding--auto .vrd-branding__img--light {
        display: none;
      }
      .vrd-branding--auto .vrd-branding__img--dark {
        display: block;
      }
    }
    /* Branding in header (banner/modal): no top spacing, visible at top */
    .vrd-banner__header .vrd-branding,
    .vrd-modal__header .vrd-branding {
      margin-top: 0;
      padding-top: 0;
      border-top: none;
    }
    .vrd-banner__header .vrd-branding {
      margin-bottom: 8px;
      padding-bottom: 8px;
      border-bottom: 1px solid var(--vrd-border);
    }
    .vrd-modal__header .vrd-branding {
      margin-bottom: 8px;
    }
    /* Branding inside header-right (grouped with close): no extra margin */
    .vrd-modal__header-right .vrd-branding {
      margin-bottom: 0;
      margin-left: 0;
    }

    /* ── Floating action button ── */
    .vrd-fab-wrap {
      position: fixed;
      z-index: 2147483639;
    }
    .vrd-fab-wrap--bottom-right {
      bottom: 20px;
      right: 20px;
    }
    .vrd-fab-wrap--bottom-left {
      bottom: 20px;
      left: 20px;
    }
    .vrd-fab-wrap--top-right {
      top: 20px;
      right: 20px;
    }
    .vrd-fab-wrap--top-left {
      top: 20px;
      left: 20px;
    }
    .vrd-fab {
      position: relative;
      width: 48px;
      height: 48px;
      border-radius: 50%;
      border: none;
      background: var(--vrd-accent);
      color: #fff;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 12px rgba(0,0,0,0.15);
      transition: filter 0.15s, transform 0.1s;
      padding: 0;
    }
    .vrd-fab:hover {
      filter: brightness(1.1);
    }
    .vrd-fab:active {
      transform: scale(0.98);
    }
    .vrd-fab:disabled {
      cursor: not-allowed;
      filter: none;
    }
    .vrd-fab__img {
      display: block;
      width: 28px;
      height: 28px;
      object-fit: contain;
      transition: opacity 0.15s;
    }
    .vrd-fab--loading .vrd-fab__img {
      opacity: 0;
    }
    .vrd-fab__spinner {
      display: none;
      position: absolute;
      width: 22px;
      height: 22px;
      border: 3px solid rgba(255, 255, 255, 0.35);
      border-top-color: #fff;
      border-radius: 50%;
      animation: vrd-spin 0.65s linear infinite;
    }
    .vrd-fab--loading .vrd-fab__spinner {
      display: block;
    }

    /* ── Consent settings panel ── */
    .vrd-consent-panel-overlay {
      align-items: center;
      justify-content: center;
    }
    .vrd-consent-panel {
      background: var(--vrd-bg);
      border-radius: 12px;
      max-width: 420px;
      width: 90vw;
      max-height: 85vh;
      overflow-y: auto;
      box-shadow: 0 8px 30px rgba(0,0,0,0.18);
      animation: vrd-scale-in 0.25s ease-out;
    }
    .vrd-consent-panel__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 20px 24px 12px;
      border-bottom: 1px solid var(--vrd-border);
    }
    .vrd-consent-panel__title {
      font-size: 18px;
      font-weight: 600;
      margin: 0;
    }
    .vrd-consent-panel__body {
      padding: 16px 24px 20px;
    }
    .vrd-consent-panel__state-heading {
      font-size: 13px;
      font-weight: 600;
      color: var(--vrd-muted);
      margin: 0 0 8px;
    }
    .vrd-consent-panel__state-list {
      margin-bottom: 12px;
    }
    .vrd-consent-panel__state-row {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      padding: 6px 0;
    }
    .vrd-consent-panel__icon--granted {
      color: var(--vrd-accent);
      font-weight: bold;
    }
    .vrd-consent-panel__icon--revoked {
      color: var(--vrd-muted);
    }
    .vrd-consent-panel__state-label {
      flex: 1;
    }
    .vrd-consent-panel__state-meta {
      font-size: 11px;
      color: var(--vrd-muted);
    }
    .vrd-consent-panel__details {
      margin: 12px 0;
    }
    .vrd-consent-panel__details-toggle {
      background: none;
      border: none;
      color: var(--vrd-accent);
      cursor: pointer;
      font-size: 13px;
      padding: 4px 0;
    }
    .vrd-consent-panel__details-toggle:hover {
      text-decoration: underline;
    }
    .vrd-consent-panel__details-content {
      font-size: 12px;
      color: var(--vrd-muted);
      margin-top: 8px;
    }
    .vrd-consent-panel__details-content--hidden {
      display: none;
    }
    .vrd-consent-panel__detail-row {
      margin: 4px 0;
    }
    .vrd-consent-panel__actions {
      display: flex;
      gap: 8px;
      margin-top: 16px;
    }
    .vrd-consent-panel__actions .vrd-btn {
      flex: 1;
    }

    /* ── Responsive ── */
    @media (max-width: 600px) {
      .vrd-banner {
        flex-direction: column;
        padding: 14px 16px;
        gap: 10px;
      }
      .vrd-banner__main {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
        flex: 0 1 auto;
      }
      .vrd-banner__body {
        flex: 0 1 auto;
        min-width: 0;
        width: 100%;
      }
      .vrd-banner__title {
        margin: 0 0 2px;
      }
      .vrd-banner__desc {
        margin: 0;
      }
      .vrd-banner__header .vrd-branding {
        margin-bottom: 6px;
        padding-bottom: 6px;
      }
      .vrd-banner__actions {
        width: 100%;
      }
      .vrd-btn {
        flex: 1;
      }
      .vrd-modal {
        width: 95vw;
        max-height: 90vh;
      }
    }
  `;
}
